import React from 'react';
import { Pressable, StyleSheet, Text, View, type PressableStateCallbackType } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import type { PlannedMeal } from '../../redux/mealPlanSlice';
import type { OnboardingIdea } from '../../services/onboardingWeek';
import { resolveRecipeTitle } from '../../utils/recipeTitle';
import { decodeHtmlEntities } from '../../utils/stripInstructionHtml';
import { RemoteImage } from '../shared/RemoteImage';

interface PlanProps {
  kind: 'plan';
  meals: PlannedMeal[];
  dayLabel: (iso: string) => string;
  onPress: () => void;
}
interface IdeasProps {
  kind: 'ideas';
  ideas: OnboardingIdea[];
  onPress: () => void;
  onPickIdea: (idea: OnboardingIdea) => void;
}
type Props = PlanProps | IdeasProps;

type Hoverable = PressableStateCallbackType & { hovered?: boolean };

/**
 * The two answers to « Que voulez-vous faire maintenant ? ». Each card leads with
 * its label, then shows what that answer gives: the plan card the first three
 * generated dinners, the ideas card three recipes for tonight (#721).
 *
 * The plan card is one button. The ideas card, when it has recipes, is a
 * label button plus one button per recipe — siblings, never nested, because a
 * child Pressable un-hovers its parent on react-native-web. With no recipes
 * it falls back to the search-bar drawing and is one button again.
 */
export function DirectionCard(props: Props) {
  const { t, i18n } = useTranslation();
  const isPlan = props.kind === 'plan';
  const ideas = props.kind === 'ideas' ? props.ideas : [];
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  // D1 (decision 2026-09-17): the label reads as a button bar, so each card is
  // understood as a button. Plan is the filled one, ideas the tonal one.
  // #721: the bar sits on top — below the preview, the question's two answers
  // were the last thing on each card, and the ideas preview read as a form.
  const cta = (
    <View style={[styles.cta, !isPlan && styles.ctaGhost]}>
      <View style={styles.ctaText}>
        <Text style={[styles.ctaTitle, !isPlan && styles.ctaGhostTitle]}>
          {t(isPlan ? 'onboarding.direction.planCta' : 'onboarding.direction.ideasCta')}
        </Text>
        <Text style={[styles.ctaSub, !isPlan && styles.ctaGhostSub]}>
          {t(isPlan
            ? 'onboarding.direction.planSub'
            : ideas.length > 0 ? 'onboarding.direction.ideasSubPicked' : 'onboarding.direction.ideasSub')}
        </Text>
      </View>
      <View style={[styles.ctaArrow, !isPlan && styles.ctaGhostArrow]}>
        <Feather name="arrow-right" size={18} color={isPlan ? '#ffffff' : Paprika.accent} />
      </View>
    </View>
  );

  if (props.kind === 'ideas' && ideas.length > 0) {
    return (
      <View style={styles.card} testID="onb-direction-ideas-card">
        <Pressable onPress={props.onPress} testID="onb-direction-ideas" accessibilityRole="button">
          {({ hovered }: Hoverable) => <View style={hovered && styles.ctaHover}>{cta}</View>}
        </Pressable>
        <View style={[styles.preview, styles.ideasPreview]}>
          {ideas.map((idea, i) => {
            const title = decodeHtmlEntities(resolveRecipeTitle(idea, lang));
            return (
              <Pressable
                key={idea.id}
                onPress={() => props.onPickIdea(idea)}
                testID={`onb-direction-idea-${i}`}
                accessibilityRole="button"
                accessibilityLabel={title}
                style={({ hovered }: Hoverable) => [styles.row, styles.ideaRow, hovered && styles.rowHover]}
              >
                <RemoteImage
                  uri={idea.image}
                  variant="thumb"
                  style={styles.thumb}
                  fallback={<View style={[styles.thumb, styles.thumbEmpty]}><Feather name="book-open" size={14} color={Paprika.textMuted} /></View>}
                />
                <View style={styles.rowText}>
                  <Text style={styles.rowTitle} numberOfLines={1}>{title}</Text>
                  {idea.readyInMinutes > 0 && (
                    <Text style={styles.rowMeta}>{t('onboarding.direction.ideaMinutes', { minutes: idea.readyInMinutes })}</Text>
                  )}
                </View>
                <Feather name="chevron-right" size={16} color={Paprika.textMuted} />
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  }

  return (
    <Pressable
      onPress={props.onPress}
      testID={isPlan ? 'onb-direction-plan' : 'onb-direction-ideas'}
      accessibilityRole="button"
      style={({ hovered }: Hoverable) => [styles.card, hovered && styles.cardHover]}
    >
      {cta}
      <View style={styles.preview}>
        {props.kind === 'plan' ? (
          props.meals.slice(0, 3).map((m) => (
            <View key={m.id} style={styles.row}>
              <View style={styles.rowDot} />
              <View style={styles.rowText}>
                <Text style={styles.rowDay}>{props.dayLabel(m.scheduledFor)}</Text>
                <Text style={styles.rowTitle} numberOfLines={1}>
                  {m.recipe ? decodeHtmlEntities(resolveRecipeTitle(m.recipe, lang)) : t('onboarding.direction.nothingPlanned')}
                </Text>
              </View>
            </View>
          ))
        ) : (
          <>
            <View style={styles.searchBar}>
              <Feather name="search" size={14} color={Paprika.textMuted} />
              <Text style={styles.searchText}>{t('onboarding.direction.searchPlaceholder')}</Text>
            </View>
            <View style={styles.chips}>
              {(['quick', 'fridge', 'rated', 'veg'] as const).map((k) => (
                <View key={k} style={styles.chip}><Text style={styles.chipText}>{t(`onboarding.direction.chips.${k}`)}</Text></View>
              ))}
            </View>
          </>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1, minHeight: 200, borderRadius: 18, overflow: 'hidden', backgroundColor: Paprika.cardBg,
    borderWidth: 1.5, borderColor: Paprika.border,
    shadowColor: 'rgba(82, 68, 60, 1)', shadowOpacity: 0.08, shadowRadius: 18, shadowOffset: { width: 0, height: 8 },
  },
  cardHover: { borderColor: Paprika.accent, transform: [{ translateY: -3 }] },
  preview: { flex: 1, padding: 14, gap: 10, backgroundColor: Paprika.bgSunk },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: Paprika.cardBg, borderRadius: 10, padding: 10 },
  rowHover: { backgroundColor: Paprika.brandSoft },
  ideasPreview: { gap: 8 },
  ideaRow: { paddingVertical: 7 },
  rowDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: Paprika.accent },
  rowText: { flex: 1, minWidth: 0 },
  rowDay: { fontFamily: 'Inter-SemiBold', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: Paprika.textMuted },
  rowTitle: { fontFamily: 'Inter-Medium', fontSize: 13, color: Paprika.textPrimary },
  rowMeta: { fontFamily: 'Inter-Regular', fontSize: 11, color: Paprika.textMuted, marginTop: 1 },
  thumb: { width: 34, height: 34, borderRadius: 8 },
  thumbEmpty: { backgroundColor: Paprika.bgSunk, alignItems: 'center', justifyContent: 'center' },
  searchBar: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: Paprika.cardBg, borderRadius: 12, padding: 12 },
  searchText: { fontFamily: 'Inter-Regular', fontSize: 13, color: Paprika.textMuted },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 999, backgroundColor: Paprika.cardBg },
  chipText: { fontFamily: 'Inter-Medium', fontSize: 12, color: Paprika.textSecondary },
  cta: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, paddingHorizontal: 16, backgroundColor: Paprika.accent },
  ctaHover: { opacity: 0.9 },
  ctaGhost: { backgroundColor: Paprika.brandSoft, borderBottomWidth: 1.5, borderBottomColor: Paprika.accent },
  ctaText: { flex: 1, minWidth: 0, gap: 1 },
  ctaTitle: { fontFamily: 'Inter-SemiBold', fontSize: 15, color: '#ffffff' },
  ctaGhostTitle: { color: Paprika.accent },
  ctaSub: { fontFamily: 'Inter-Regular', fontSize: 12, color: 'rgba(255,255,255,0.85)' },
  ctaGhostSub: { color: Paprika.textSecondary },
  ctaArrow: { width: 38, height: 38, borderRadius: 19, backgroundColor: 'rgba(255,255,255,0.22)', alignItems: 'center', justifyContent: 'center' },
  ctaGhostArrow: { backgroundColor: Paprika.glassWhite },
});
